type AssignmentFn = (seed: string | number, variants: string[]) => string;

interface ABTestConfig {
  testName: string;
  variants: string[];
  weights?: number[];
  storageKeyPrefix?: string;
  forceReassignment?: boolean;
  seed?: string | number;
  assignmentFn?: AssignmentFn;
}

export class ABTestManager {
  private testName: string;
  private variants: string[];
  private weights?: number[];
  private storageKey: string;
  private assignedVariant: string | null = null;
  private seed?: string | number;
  private assignmentFn?: AssignmentFn;

  constructor(config: ABTestConfig) {
    const {
      testName,
      variants,
      weights,
      storageKeyPrefix = 'abtest_',
      forceReassignment = false,
      seed,
      assignmentFn,
    } = config;

    if (weights && weights.length !== variants.length) {
      throw new Error("Weights array length must match variants array length");
    }

    this.testName = testName;
    this.variants = variants;
    this.weights = weights;
    this.storageKey = `${storageKeyPrefix}${testName}`;
    this.seed = seed;
    this.assignmentFn = assignmentFn;

    this.assignedVariant = forceReassignment ? this.assignVariant() : this.loadVariant() || this.assignVariant();
  }

  private loadVariant(): string | null {
    return ABTestManager.readStoredVariant(this.storageKey, this.variants);
  }

  /** The variant already stored for a test, without assigning one; null if none (or an invalid one, which is dropped). */
  public static getStoredVariant(testName: string, variants: string[], storageKeyPrefix = 'abtest_'): string | null {
    return ABTestManager.readStoredVariant(`${storageKeyPrefix}${testName}`, variants);
  }

  private static readStoredVariant(storageKey: string, variants: string[]): string | null {
    if (typeof window === 'undefined') return null;
    const storedVariant = localStorage.getItem(storageKey);
    if (storedVariant && !variants.includes(storedVariant)) {
      localStorage.removeItem(storageKey);
      return null;
    }
    return storedVariant;
  }

  private assignVariant(): string {
    const variant = this.pickVariant();
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.storageKey, variant);
    }
    return variant;
  }

  private pickVariant(): string {
    if (this.seed !== undefined && this.assignmentFn) {
      return this.assignmentFn(this.seed, this.variants);
    }

    if (!this.weights) {
      const randomIndex = Math.floor(Math.random() * this.variants.length);
      return this.variants[randomIndex];
    }

    const rand = Math.random();
    let cumulative = 0;

    for (let i = 0; i < this.variants.length; i++) {
      cumulative += this.weights[i];
      if (rand < cumulative) return this.variants[i];
    }

    return this.variants[this.variants.length - 1];
  }

  public getVariant(): string {
    return this.assignedVariant!;
  }

  public setVariant(variant: string): void {
    if (!this.variants.includes(variant)) {
      throw new Error(`Invalid variant: ${variant}`);
    }
    this.assignedVariant = variant;
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.storageKey, variant);
    }
  }

  public resetVariant(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(this.storageKey);
    }
    this.assignedVariant = this.assignVariant();
  }
}
